import { useEffect, useId, useRef, useState } from "react";
import { Alert, Button, Input, Modal, Table, Typography } from "antd";
import { Search } from "lucide-react";
import { edge } from "../../lib/supabase.js";
import { AppEmpty } from "../../components/Feedback.jsx";

export function SchoolCreateModal({ open, schoolLevel, title = "학교 추가", onCancel, onSaved, className = "" }) {
  const helpId = useId();
  const [keyword, setKeyword] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState("");
  const [savingCode, setSavingCode] = useState("");
  const [retryAttempt, setRetryAttempt] = useState(0);
  const inputValue = useRef("");
  const saving = useRef(false);
  const searchVersion = useRef(0);
  const lastRequestAt = useRef(0);

  useEffect(() => {
    searchVersion.current++;
    setKeyword(""); setResults([]); setError(""); setSearched(false); setHasMore(false);
    setSearching(false); inputValue.current = "";
  }, [open, schoolLevel]);

  useEffect(() => {
    const version = ++searchVersion.current;
    const name = keyword.trim();
    if (!open || name.length < 2) { setSearching(false); return; }
    setSearching(true); setSearched(false); setHasMore(false); setError("");
    // Debounce typing and respect the server's one-search-per-second guard.
    const timer = setTimeout(async () => {
      lastRequestAt.current = Date.now();
      try {
        const result = await edge("school-directory", { action: "search", keyword: name });
        if (version !== searchVersion.current) return;
        setResults(result.schools.filter((school) => !schoolLevel || school.schoolLevel === schoolLevel));
        setHasMore(result.hasMore); setSearched(true);
      } catch (failure) {
        if (version === searchVersion.current) setError(failure.message);
      } finally {
        if (version === searchVersion.current) setSearching(false);
      }
    }, Math.max(400, 1100 - (Date.now() - lastRequestAt.current)));
    return () => { clearTimeout(timer); searchVersion.current++; };
  }, [open, keyword, schoolLevel, retryAttempt]);

  function updateKeyword(value) {
    // IME may keep the final syllable composing until blur, and repeat its value
    // on compositionend. Search after a typing pause without requiring blur.
    if (value === inputValue.current) return;
    inputValue.current = value;
    searchVersion.current++;
    setKeyword(value);
    setResults([]); setError(""); setSearched(false); setHasMore(false);
  }

  async function selectSchool(school) {
    if (saving.current) return;
    saving.current = true;
    setSavingCode(school.sourceCode); setError("");
    let saved = false;
    try {
      const result = await edge("school-directory", { action: "add", sourceCode: school.sourceCode });
      saved = true;
      await onSaved(result.school);
    } catch {
      setError(saved
        ? "학교는 저장됐지만 목록을 갱신하지 못했습니다. 같은 학교를 다시 선택하면 중복 없이 목록을 확인합니다."
        : "학교를 추가하지 못했습니다. 잠시 후 다시 선택해 주세요.");
    } finally {
      saving.current = false; setSavingCode("");
    }
  }

  const emptyText = keyword.trim().length < 2
    ? "학교명을 2자 이상 입력해 주세요."
    : error && !searched ? "검색 결과를 불러오지 못했습니다."
    : searched ? "검색 결과가 없습니다. 학교명을 다시 확인해 주세요." : "학교를 검색하고 있습니다.";
  return <Modal className={`school-create-modal ${className}`} title={title} open={open} width={720}
    onCancel={savingCode ? undefined : onCancel} closable={!savingCode}
    maskClosable={!savingCode} keyboard={!savingCode}
    footer={null}>
    <Typography.Paragraph className="school-management-hint">
      학교명을 입력하면 검색 결과가 표시됩니다. 지역을 확인한 뒤 선택하세요.
      {schoolLevel && " 학생의 과정(" + schoolLevel + ")에 맞는 학교만 표시합니다."}
    </Typography.Paragraph>
    <Input prefix={<Search size={15} />} allowClear aria-describedby={helpId}
      aria-label="학교명 검색" placeholder="학교명 검색(2자이상)"
      value={keyword} maxLength={80} disabled={Boolean(savingCode)}
      onCompositionEnd={(event) => updateKeyword(event.currentTarget.value)}
      onChange={(event) => updateKeyword(event.target.value)} />
    <span id={helpId} className="school-management-hint">학교명은 2자 이상 입력해 주세요. 검색만으로 학교가 추가되지는 않습니다.</span>
    {error && <Alert type="error" showIcon message={error}
      action={!searched && !savingCode && <Button size="small" disabled={searching} onClick={() => setRetryAttempt((value) => value + 1)}>재시도</Button>} />}
    <Table className="management-list-table school-search-table" size="small" rowKey="sourceCode"
      dataSource={results} loading={searching} pagination={false}
      scroll={{ x: 560, y: 320 }} locale={{ emptyText: <AppEmpty description={emptyText} compact /> }} columns={[
        { title: "학교명", dataIndex: "name" },
        { title: "과정", dataIndex: "schoolLevel", width: 64 },
        { title: "지역", render: (_, school) => school.city + " " + school.district },
        { title: "추가", width: 92, fixed: "right", align: "center", render: (_, school) => <Button className="school-row-action" type="primary" size="small"
          loading={savingCode === school.sourceCode} disabled={Boolean(savingCode) || searching}
          onClick={() => selectSchool(school)}>추가</Button> },
      ]} />
    {hasMore && <Typography.Paragraph className="school-management-hint">
      결과가 많아 최대 50개만 표시합니다. 학교명을 더 자세히 입력해 주세요.
    </Typography.Paragraph>}
  </Modal>;
}
